import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useJob } from "../context";
import { fetchRevenueCalculationDashboard } from "../api";
import { EvtSectionHeader } from "components/header";
import { EvtStatsCard } from "components/stats/display-card";
import { EvtStatsCardPlaceholder } from "components/stats/placeholder";
import QueryError from "components/query-error";
import { DisplayHours } from "components/display/hours";
import { DisplayPercentage } from "components/display/percentage";
import { DisplayDate } from "components/display/date";
import { TOOLTIPS } from "./constants";
import { usePermissions } from "contexts/permission";
import { EvtLockedSection } from "components/stats/locked-section";
import {
  DASHBOARD_VALIDATION_TITLE,
  getDashboardValidationIssues,
} from "@/modules/work/shared/dashboard-validation";
import CalendarDatePicker from "ui/components/comp-507";

const JobStatsPlaceholder = () => (
  <section className="mt-3.5">
    <EvtSectionHeader title="Overview" />
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
      <EvtStatsCardPlaceholder />
      <EvtStatsCardPlaceholder />
      <EvtStatsCardPlaceholder />
      <EvtStatsCardPlaceholder />
      <EvtStatsCardPlaceholder />
      <EvtStatsCardPlaceholder />
    </div>
  </section>
);

export const JobDashboardStatsSection = () => {
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  });
  const { jobId } = useParams();
  const { type } = useJob();

  const permissions = usePermissions();

  if (type === "free") {
    return null;
  }

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  const isDateRangeComplete = Boolean(dateRange?.from && dateRange?.to);
  const startDate = isDateRangeComplete ? dateRange.from : undefined;
  const endDate = isDateRangeComplete ? dateRange.to : undefined;

  const { data, isLoading, isError, refetch } = useFetch({
    queryKey: ["revenue-calculation-dashboard", jobId, startDate, endDate],
    queryFn: () =>
      fetchRevenueCalculationDashboard({
        jobId,
        startDate,
        endDate,
      }),
    enabled: !!jobId,
  });

  if (isLoading) {
    return <JobStatsPlaceholder />;
  }
  const validationIssues = getDashboardValidationIssues(
    data?.validation,
    "job",
    jobId,
  );
  if (validationIssues.length) {
    return (
      <EvtLockedSection
        title={DASHBOARD_VALIDATION_TITLE}
        items={validationIssues}
      >
        {/* Placeholder values only: real stats are never rendered while invalid. */}
        <JobStatsPlaceholder />
      </EvtLockedSection>
    );
  }

  if (isError) {
    return (
      <section className="mt-3.5">
        <EvtSectionHeader title="Overview" />
        <div className="px-4 md:px-6 mt-2.5">
          <QueryError
            message="Failed to load dashboard metrics."
            onRetry={refetch}
          />
        </div>
      </section>
    );
  }

  const renderFixedCards = (stats) => ({
    overview: (
      <>
        <EvtStatsCard
          label="Job Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={TOOLTIPS.fixed.overview.budgetVsAchieved}
        />

        <EvtStatsCard
          label="Allocated"
          value={<DisplayHours value={stats?.allocated_hours} />}
          info={TOOLTIPS.fixed.overview.allocatedHours}
        />
        <EvtStatsCard
          label="Consumed vs Spent"
          value={
            <>
              <DisplayHours value={stats?.consumed_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.spent_hours} hoursOnly />
            </>
          }
          info={TOOLTIPS.fixed.overview.consumedVsSpent}
        />

        <EvtStatsCard
          label="Remaining Allocated Hours"
          value={<DisplayHours value={stats?.remaining_allocated_hours} />}
          info={TOOLTIPS.fixed.overview.remainingAllocatedHours}
        />

        <EvtStatsCard
          label="Effort Progress"
          value={
            <DisplayPercentage
              value={stats?.effort_progress}
              precision={0}
              withSuffix
            />
          }
          info={TOOLTIPS.fixed.overview.effortProgress}
        />
      </>
    ),
  });

  const renderHourlyCards = (stats) => ({
    overview: (
      <>
        <EvtStatsCard
          label="Job Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={TOOLTIPS.hourly.overview.budgetVsAchieved}
        />

        <EvtStatsCard
          label="Next Billing Date"
          value={<DisplayDate value={stats?.next_billing_date} />}
          info={TOOLTIPS.hourly.overview.nextBillingDate}
        />
        <EvtStatsCard
          label="Approved vs Logged"
          value={
            <>
              <DisplayHours value={stats?.approved_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.logged_hours} hoursOnly />
            </>
          }
          info={TOOLTIPS.hourly.overview.loggedVsApproved}
        />
        {canReadAmount && (
          <EvtStatsCard
            label="Milestone vs Billable Difference"
            value={
              <DisplayHours value={stats?.milestone_config_hours_difference} />
            }
            info={TOOLTIPS.hourly.overview.milestoneConfigHoursDifference}
          />
        )}
      </>
    ),
    lifetimeBillableSummary: (
      <>
        <EvtStatsCard
          label="Billable"
          value={<DisplayHours value={stats?.billable_hours} />}
          info={TOOLTIPS.hourly.lifetimeBillableSummary.billable}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayHours value={stats?.billed_hours} />}
          info={TOOLTIPS.hourly.lifetimeBillableSummary.billed}
        />
        <EvtStatsCard
          label="Backlog / Advance"
          value={<DisplayHours value={stats?.billing_difference} />}
          info={TOOLTIPS.hourly.lifetimeBillableSummary.backlogAdvance}
        />
      </>
    ),
    lastBillableSummary: (
      <>
        <EvtStatsCard
          label="Billable"
          value={<DisplayHours value={stats?.last_period_billable_hours} />}
          info={TOOLTIPS.hourly.lastBillableSummary.billable}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayHours value={stats?.last_period_billed_hours} />}
          info={TOOLTIPS.hourly.lastBillableSummary.billed}
        />
      </>
    ),
  });

  const renderDedicatedCards = (stats) => ({
    overview: (
      <>
        <EvtStatsCard
          label="Job Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={TOOLTIPS.dedicated.overview.budgetVsAchieved}
        />

        <EvtStatsCard
          label="Next Billing Date"
          value={<DisplayDate value={stats?.next_billing_date} />}
          info={TOOLTIPS.dedicated.overview.nextBillingDate}
        />
        <EvtStatsCard
          label="Timeline (In Months)"
          value={`${stats?.timeline?.completed_months ?? 0} / ${stats?.timeline?.total_months ?? 0}`}
          info={TOOLTIPS.dedicated.overview.timelineInMonths}
        />
      </>
    ),
    lifetimeBillableSummary: (
      <>
        <EvtStatsCard
          label="Total"
          value={<DisplayHours value={stats?.total_hours} />}
          info={TOOLTIPS.dedicated.lifetimeBillableSummary.total}
        />
        <EvtStatsCard
          label="Consumed vs Spent"
          value={
            <>
              <DisplayHours value={stats?.consumed_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.spent_hours} hoursOnly />
            </>
          }
          info={TOOLTIPS.dedicated.lifetimeBillableSummary.consumedVsSpent}
        />
      </>
    ),
    activeBillable: (
      <>
        <EvtStatsCard
          label="Total"
          value={<DisplayHours value={stats?.active_billable_working_hours} />} // TODO: check this calculation
          info={TOOLTIPS.dedicated.activeBillable.total}
        />
        <EvtStatsCard
          label="Consumed vs Spent"
          value={
            <>
              <DisplayHours
                value={stats?.active_billable_consumed_hours}
                hoursOnly
              />{" "}
              /{" "}
              <DisplayHours
                value={stats?.active_billable_spent_hours}
                hoursOnly
              />
            </>
          }
          info={TOOLTIPS.dedicated.activeBillable.consumedVsSpent}
        />
      </>
    ),
  });

  const renderBucketCards = (stats) => ({
    overview: (
      <>
        <EvtStatsCard
          label="Job Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={TOOLTIPS.bucket.overview.budgetVsAchieved}
        />

        <EvtStatsCard
          label="Bucket Size"
          value={<DisplayHours value={stats?.bucket_size} />}
          info={TOOLTIPS.bucket.overview.total}
        />
        <EvtStatsCard
          label="Redeemed"
          value={<DisplayHours value={stats?.redeemed_hours} />}
          info={TOOLTIPS.bucket.overview.redeemed}
        />
        <EvtStatsCard
          label="Target"
          value={
            <DisplayHours value={stats?.hours_to_redeem_for_target_margin} />
          }
          info={TOOLTIPS.bucket.overview.targetHoursToRedeem}
        />
        <EvtStatsCard
          label="Remaining"
          value={<DisplayHours value={stats?.remaining_hours} />}
          info={TOOLTIPS.bucket.overview.remaining}
        />
        <EvtStatsCard
          label="Billable"
          value={<DisplayHours value={stats?.billable_hours} />}
          info={TOOLTIPS.bucket.overview.billable}
        />
        <EvtStatsCard
          label="Approved vs Logged"
          value={
            <>
              <DisplayHours value={stats?.approved_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.logged_hours} hoursOnly />
            </>
          }
          info={TOOLTIPS.bucket.overview.loggedVsApproved}
        />
      </>
    ),
  });

  const renderRecurringCards = (stats) => ({
    overview: (
      <>
        <EvtStatsCard
          label="Job Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={TOOLTIPS.recurring.overview.budgetVsAchieved}
        />

        <EvtStatsCard
          label="Logged vs Approved"
          value={
            <>
              <DisplayHours value={stats?.logged_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.approved_hours} hoursOnly />
            </>
          }
          info={TOOLTIPS.recurring.overview.loggedVsApproved}
        />
        <EvtStatsCard
          label="Next Renewal Date"
          value={<DisplayDate value={stats?.next_renewal_date} />}
          info={TOOLTIPS.recurring.overview.nextRenewalDate}
        />
      </>
    ),
    activeBillable: (
      <>
        <EvtStatsCard
          label="Billing Frequency"
          value={stats?.billing_frequency}
          className="capitalize"
          info={TOOLTIPS.recurring.activeBillable.billingFrequency}
        />
        <EvtStatsCard
          label="Interval"
          value={stats?.billing_interval ?? 0}
          info={TOOLTIPS.recurring.activeBillable.interval}
        />
        <EvtStatsCard
          label="Active Interval Progress"
          value={`${stats?.active_interval_progress?.completed_days ?? 0} / ${stats?.active_interval_progress?.total_days ?? 0}`}
          description="Completed / Total Days"
          info={TOOLTIPS.recurring.activeBillable.activeIntervalProgress}
        />
      </>
    ),
  });

  const getCards = () => {
    switch (type) {
      case "fixed":
        return renderFixedCards(data);
      case "hourly":
        return renderHourlyCards(data);
      case "dedicated":
        return renderDedicatedCards(data);
      case "bucket":
        return renderBucketCards(data);
      case "recurring":
        return renderRecurringCards(data);
      default:
        return {};
    }
  };

  const {
    overview,
    lifetimeBillableSummary,
    lastBillableSummary,
    activeBillable,
  } = getCards();

  return (
    <>
      {overview && (
        <section className="mt-3.5">
          <EvtSectionHeader title="Overview">
            <CalendarDatePicker date={dateRange} setDate={setDateRange} />
          </EvtSectionHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {overview}
          </div>
        </section>
      )}

      {lifetimeBillableSummary && (
        <section className="mt-3.5">
          <EvtSectionHeader title="Lifetime Billable Summary" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {lifetimeBillableSummary}
          </div>
        </section>
      )}

      {lastBillableSummary && (
        <section className="mt-3.5">
          <EvtSectionHeader title="Last Billable Summary" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {lastBillableSummary}
          </div>
        </section>
      )}

      {activeBillable && (
        <section className="mt-3.5">
          <EvtSectionHeader title="Active Billable" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {activeBillable}
          </div>
        </section>
      )}
    </>
  );
};
